<template>
    <main class="settings-v2" role="main" v-if="loading">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('menu.companies.settings') }}</div>
        </portal>

        <header class="settings-hero">
            <div>
                <h1 class="settings-hero-title">{{ $t('menu.companies.settings') }}</h1>
                <p class="settings-hero-sub">{{ $t('menu.companies.settings_subtitle') }}</p>
            </div>
        </header>

        <nav class="st-tabs">
            <button
                v-for="tab in visibleTabs"
                :key="tab.key"
                type="button"
                class="st-tab-btn"
                :class="{ 'is-active': activeTab === tab.key }"
                @click="activeTab = tab.key"
            >
                <i :class="tab.icon" class="st-tab-icon"></i>
                <span>{{ tab.label }}</span>
            </button>
        </nav>

        <div class="st-tab-content">
            <SettingsContact v-show="activeTab === 'general'" />
            <SettingsMail v-show="activeTab === 'emails'" />
            <SettingsWhatsApp v-show="activeTab === 'whatsapp'" />
            <SettingsTokens
                v-if="isAdmin"
                v-show="activeTab === 'tokens'"
                :companyId="companyInfo && companyInfo.id"
            />
        </div>
    </main>
</template>

<script>
import { mapActions } from 'vuex'
import SettingsContact from '@/components/Model/Company/Settings/SettingsContact'
import SettingsMail from '@/components/Model/Company/Settings/SettingsMail'
import SettingsTokens from '@/components/Model/Company/Settings/SettingsTokens'
import SettingsWhatsApp from '@/components/Model/Company/Settings/SettingsWhatsApp'

export default {
    name: 'Settings',
    components: {
        SettingsContact,
        SettingsMail,
        SettingsTokens,
        SettingsWhatsApp
    },
    data() {
        return {
            admin_type: 777,
            loading: false,
            activeTab: 'general',
            companyInfo: null,
            userInfo: null
        }
    },
    computed: {
        isAdmin() {
            return this.userInfo && this.userInfo.user_type == this.admin_type
        },
        visibleTabs() {
            const tabs = [
                { key: 'general', label: this.$t('companies.settings.tabs.general'), icon: 'fal fa-cog' },
                { key: 'emails', label: this.$t('companies.settings.tabs.emails'), icon: 'fal fa-envelope' },
                { key: 'whatsapp', label: 'WhatsApp', icon: 'fab fa-whatsapp' }
            ]
            if (this.isAdmin) {
                tabs.push({ key: 'tokens', label: this.$t('companies.settings.tabs.tokens'), icon: 'fal fa-key' })
            }
            return tabs
        }
    },
    created() {
        this.getCompanySettingsHandler()
    },
    mounted() {
        this.companyInfo = JSON.parse(localStorage.getItem('company'))
        this.userInfo = JSON.parse(localStorage.getItem('user'))
    },
    methods: {
        ...mapActions('company', ['getCompanySettings']),
        getCompanySettingsHandler() {
            this.getCompanySettings()
                .then(() => {
                    this.loading = true
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
        }
    }
}
</script>

<style scoped>
.settings-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.settings-hero {
    margin-bottom: 24px;
}

.settings-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.settings-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

/* Tab bar */
.st-tabs {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    background: #f1f5f9;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 4px;
    margin-bottom: 20px;
}

.st-tab-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 16px;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #64748b;
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--v2-font-sans);
    transition: all 0.15s ease;
    white-space: nowrap;
}

.st-tab-btn:hover {
    color: #0891b2;
    background: rgba(8, 145, 178, 0.06);
}

.st-tab-btn.is-active {
    background: #fff;
    color: #0891b2;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.st-tab-icon {
    font-size: 0.82rem;
    flex-shrink: 0;
    color: #94a3b8;
    transition: color 0.15s ease;
}

.st-tab-btn:hover .st-tab-icon,
.st-tab-btn.is-active .st-tab-icon {
    color: #0891b2;
}

.st-tab-content {
    width: 100%;
}

@media (max-width: 900px) {
    .settings-v2 {
        padding: 16px 18px 32px;
    }

    .settings-hero-title {
        font-size: 1.375rem;
    }

    .st-tabs {
        width: 100%;
        overflow-x: auto;
    }
}
</style>

<style>
/* Shared settings styles */
.st-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.st-card--full {
    grid-column: 1 / -1;
}

.st-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.st-card-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(8, 145, 178, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.st-card-icon i {
    color: #0891B2;
    font-size: 1.1rem;
}

.st-card-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.st-card-desc {
    font-size: 0.78rem;
    color: #94a3b8;
    margin: 2px 0 0;
}

.st-card-body {
    padding: 24px;
}

.st-card-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #f1f5f9;
    background: #f8fafc;
}

/* Fields */
.st-field {
    margin-bottom: 18px;
}

.st-field:last-child {
    margin-bottom: 0;
}

.st-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
}

.st-input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.88rem;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    transition: all 0.2s;
}

.st-input:hover {
    border-color: #cbd5e1;
}

.st-input:focus {
    border-color: #0891B2;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.08);
}

.st-error {
    font-size: 0.75rem;
    color: #ef4444;
    margin: 4px 0 0;
    font-weight: 500;
}

.st-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 20px;
    margin-bottom: 18px;
}

.st-grid-2 .st-field {
    margin-bottom: 0;
}

.st-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.84rem;
    font-weight: 700;
    color: #0891B2;
    margin: 20px 0 14px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
}

.st-section-title i {
    font-size: 0.88rem;
}

/* Radio group */
.st-radio-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.st-radio {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.84rem;
    color: #334155;
    cursor: pointer;
    transition: all 0.2s;
}

.st-radio:hover {
    border-color: #cbd5e1;
}

.st-radio.is-selected {
    border-color: #0891B2;
    background: rgba(8, 145, 178, 0.04);
}

.st-radio input {
    display: none;
}

.st-radio-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    flex-shrink: 0;
    transition: all 0.2s;
}

.st-radio.is-selected .st-radio-dot {
    border-color: #0891B2;
    background: #0891B2;
    box-shadow: inset 0 0 0 3px #fff;
}

/* Buttons */
.st-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 20px;
    border-radius: 10px;
    font-size: 0.84rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.st-btn--primary {
    background: #0891B2;
    color: #fff;
}

.st-btn--primary:hover {
    background: #0e7490;
    box-shadow: 0 3px 10px rgba(8, 145, 178, 0.25);
}

.st-success-msg {
    font-size: 0.78rem;
    color: #10b981;
    font-weight: 500;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

/* Token */
.st-token-code {
    font-size: 0.72rem;
    background: #f1f5f9;
    padding: 4px 8px;
    border-radius: 6px;
    color: #334155;
    word-break: break-all;
}

.st-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px;
    color: #94a3b8;
    gap: 8px;
}

.st-empty i {
    font-size: 1.5rem;
}

@media (max-width: 1024px) {
    .st-grid-2 {
        grid-template-columns: 1fr;
    }
}

/* CKEditor */
.ck-editor__editable_inline {
    min-height: 150px;
}
.ck-editor__editable {
    max-height: 500px;
}
</style>
